import type { AllHTMLAttributes, ChangeEvent, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import type { FieldContainerBaseProps } from '../field-container/field-container';
import { FieldContainer } from '../field-container/field-container';
import { List } from '../list/list';

import { DocumentUploaderInput } from './document-uploader-input';
import { DocumentUploaderModal } from './document-uploader-modal';

export interface DocumentUploaderProps
  extends FieldContainerBaseProps,
    Partial<Pick<AllHTMLAttributes<HTMLInputElement>, 'required' | 'accept'>> {
  id: string;
  // We need to pass down things to happen onChange, but need to only apply them as props to the actual input element
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
  value?: string | null;
  'data-testid'?: string;
  'aria-describedby'?: string;
  validating?: boolean;
  deleteDocumentText: ReactNode;
  uploadingText: ReactNode;
  children: ReactNode;
}

const DEBOUNCE_TIME = 200;

/**
 * Hook to wait a small amount of time before returning a true value
 * If value is false, it will return false immediately
 */
const useDebouncedTrue = (value: boolean, delay = DEBOUNCE_TIME) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  const [debouncedTrue, setDebouncedTrue] = useState(!!value);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (!value) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDebouncedTrue(false);
      return;
    }

    if (value) {
      timeoutRef.current = setTimeout(() => {
        setDebouncedTrue(true);
      }, delay);
    }
  }, [value, delay]);

  return debouncedTrue;
};

export const DocumentUploader = ({
  children,
  onClear,
  label,
  helpText,
  disabled = false,
  onChange,
  deleteDocumentText,
  uploadingText,
  validating,
  value = '',
  name,
  id,
  required = false,
  errors,
  'aria-invalid': invalid,
  'aria-describedby': errorMessageId,
  accept = 'image/jpeg,image/png,application/pdf',
  ...inputProps // we want to spread in any other props that are passed in by libraries, e.g. Conform
}: DocumentUploaderProps) => {
  // wait a small amount of time before showing the uploader
  // avoiding a flash of the uploader if it fails quickly.
  const showUploader = useDebouncedTrue(!!value);

  return (
    <>
      <FieldContainer
        htmlFor={id}
        name={name}
        label={label}
        errors={errors}
        aria-invalid={invalid}
        errorMessageId={errorMessageId}
        disabled={disabled}
        helpText={helpText}
      >
        <DocumentUploaderInput
          {...inputProps}
          onChange={onChange}
          id={id}
          disabled={disabled}
          validating={validating}
          name={name}
          required={required}
          aria-invalid={invalid}
          aria-describedby={errorMessageId}
          accept={accept}
        >
          {children}
        </DocumentUploaderInput>
      </FieldContainer>

      {showUploader ? (
        <List>
          <List.Item
            variant="uploader"
            state={validating ? 'uploading' : 'uploaded'}
            filename={value}
            uploadingText={uploadingText}
            deleteDocumentText={deleteDocumentText}
            onClear={onClear}
          />
        </List>
      ) : null}
    </>
  );
};

DocumentUploader.Modal = DocumentUploaderModal;
